export const MAX_COMPARE_PRODUCTS = 5;
export const COMPARE_PRODUCTS_STORAGE_KEY = "creatyea_compare_products";

export const hiddenProductAttributeKeys = new Set([
  "Spider_Source",
  "Spider_Source_Product_Code",
  "Spider_Source_Product_Id",
  "Spider_Source_Url",
  "Spider_Source_Image_Url",
  "Spider_Source_Category_Name",
  "Spider_Source_Category_Path",
  "TODO_Remark",
]);

/**
 * 判断商品属性是否允许在前台商品表格和对比页展示。
 * @param {string} attributeName 商品属性名称
 * @returns {boolean} 是否为前台可见属性
 */
export const isVisibleProductCompareAttribute = (attributeName = "") => {
  return (
    attributeName &&
    !attributeName.startsWith("Spider_") &&
    !hiddenProductAttributeKeys.has(attributeName)
  );
};

/**
 * 获取当前商品列表中需要展示的动态属性键。
 * @param {Array<Record<string, any>>} products 商品列表
 * @returns {string[]} 动态属性键列表
 */
export const getVisibleAttributeKeys = (products = []) => {
  const attributeKeys = [];

  products.forEach((product) => {
    Object.keys(product?.product_Attributes || {}).forEach((key) => {
      if (isVisibleProductCompareAttribute(key) && !attributeKeys.includes(key)) {
        attributeKeys.push(key);
      }
    });
  });

  return attributeKeys;
};

/**
 * 格式化商品对比属性值，空值统一显示占位符。
 * @param {any} value 商品属性值
 * @returns {string} 可展示的属性值
 */
export const formatCompareValue = (value) => {
  if (value === undefined || value === null) {
    return "-";
  }

  const text = String(value).trim();
  return text && text !== "_" && text !== "-" ? text : "-";
};

/**
 * 规范化商品对比值，用于判断多个产品属性是否相同。
 * @param {any} value 商品属性值
 * @returns {string} 规范化后的属性值
 */
export const normalizeCompareValue = (value) => {
  return formatCompareValue(value).toLowerCase();
};

/**
 * 判断指定取值函数在所有商品中是否返回相同值。
 * @param {Array<Record<string, any>>} products 已选商品列表
 * @param {(product: Record<string, any>) => any} getValue 商品取值函数
 * @returns {boolean} 是否全部相同
 */
export const isSharedCompareValue = (products = [], getValue) => {
  if (products.length < 2) {
    return false;
  }

  const values = products.map((product) => normalizeCompareValue(getValue(product)));
  return values.every((value) => value === values[0]);
};

/**
 * 获取商品主图地址，缺失时返回默认占位图。
 * @param {Record<string, any>} product 商品对象
 * @returns {string} 商品图片地址
 */
export const getProductImageUrl = (product) => {
  return product?.image?.[0] || "/img/imgnotavailable.png";
};

/**
 * 获取商品制造商料号，缺失时回退到商品名称。
 * @param {Record<string, any>} product 商品对象
 * @returns {string} 制造商料号文本
 */
export const getManufacturerPartNumber = (product) => {
  return formatCompareValue(product?.Manufacturer_Part_Number || product?.name);
};

/**
 * 获取商品描述，缺失时回退到商品名称。
 * @param {Record<string, any>} product 商品对象
 * @returns {string} 商品描述文本
 */
export const getProductDescription = (product) => {
  const description = formatCompareValue(product?.description || product?.name);
  return description.replace(/^DigiKey product\b/i, "Creatyea Product");
};

/**
 * 从浏览器会话缓存读取已选对比商品。
 * @returns {Array<Record<string, any>>} 已选对比商品列表
 */
export const readStoredCompareProducts = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const value = window.sessionStorage.getItem(COMPARE_PRODUCTS_STORAGE_KEY);
    const products = value ? JSON.parse(value) : [];
    return Array.isArray(products) ? products : [];
  } catch (error) {
    return [];
  }
};

/**
 * 把已选对比商品写入浏览器会话缓存。
 * @param {Array<Record<string, any>>} products 已选对比商品列表
 */
export const writeStoredCompareProducts = (products = []) => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(COMPARE_PRODUCTS_STORAGE_KEY, JSON.stringify(products));
};

/**
 * 从浏览器会话缓存中移除指定对比商品。
 * @param {string} productId 商品 ID
 */
export const removeStoredCompareProduct = (productId) => {
  const nextProducts = readStoredCompareProducts().filter(
    (product) => product?._id !== productId
  );
  writeStoredCompareProducts(nextProducts);
};

/**
 * 清空浏览器会话缓存中的全部对比商品。
 */
export const clearStoredCompareProducts = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(COMPARE_PRODUCTS_STORAGE_KEY);
};
